import {
  DeviceModeType,
  deviceModes,
  screenOrientations,
  useDeviceModeContext,
} from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import React, { useCallback, useMemo } from 'react'
import { CheckmarkIcon } from 'src/editor/shared/icons/checkmark-icon'
import { Monitor01Icon } from 'src/editor/shared/icons/monitor-01'
import { Phone01Icon } from 'src/editor/shared/icons/phone-01-icon'
import { PhoneRotate01Icon } from 'src/editor/shared/icons/phone-rotate-01-icon'
import { XCircleFill02Icon } from 'src/editor/shared/icons/x-circle-fill-02-icon'
import { BadgeDropdown } from 'src/editor/shared/ui-lib/badge-dropdown'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import {
  BadgeColorEnum,
  BadgeSizeEnum,
} from 'src/editor/shared/ui-lib/shared/badge-consts'
import { setIsPreviewMode } from 'src/editor/store/website-pages/website-pages-actions'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'

export const PreviewNavigation = () => {
  const {
    deviceMode,
    setDeviceMode,
    screenOrientation,
    isLandscape,
    setScreenOrientation,
    isDesktop,
    isMobile,
  } = useDeviceModeContext()
  const { t } = useTypedTranslation()
  const dispatch = useAppDispatch()

  const setCurrentDeviceMode = useCallback(
    (newDeviceMode: DeviceModeType) => {
      if (deviceMode !== newDeviceMode) {
        setDeviceMode(newDeviceMode)
      }
    },
    [setDeviceMode, deviceMode],
  )

  const toggleScreenOrientation = useCallback(() => {
    if (screenOrientation === screenOrientations.Portrait) {
      setScreenOrientation(screenOrientations.Landscape)
    } else {
      setScreenOrientation(screenOrientations.Portrait)
    }
  }, [screenOrientation, setScreenOrientation])

  const currentTitle = useMemo(() => {
    switch (deviceMode) {
      case deviceModes.Desktop:
        return t('settings_options.device_appearance.desktop')
      case deviceModes.Mobile:
        return t('settings_options.device_appearance.mobile')
      default:
        return t('settings_options.device_appearance.desktop')
    }
  }, [t, deviceMode])

  const currentDeviceIcon = useMemo(() => {
    switch (deviceMode) {
      case deviceModes.Desktop:
        return <Monitor01Icon className="h-5 w-5 fill-iconPrimary" />
      case deviceModes.Mobile:
        return <Phone01Icon className="h-5 w-5 stroke-iconPrimary" />
      default:
        return <Monitor01Icon className="h-5 w-5 fill-iconPrimary" />
    }
  }, [deviceMode])

  const handleClosePreview = useCallback(() => {
    dispatch(setIsPreviewMode(false))
    setScreenOrientation(screenOrientations.Portrait)

    // remove search params, e.g. ?preview=true on exit
    const newUrl = window.location.pathname + window.location.hash
    window.history.replaceState({}, '', newUrl)
  }, [dispatch, setScreenOrientation])

  return (
    <nav className="pointer-events-none absolute right-0 left-0 z-100 grid grid-cols-3 px-8 py-2">
      <div className="col-start-2 inline-flex items-center justify-center gap-4">
        <BadgeDropdown
          className="pointer-events-auto"
          size={BadgeSizeEnum.Large}
          color={BadgeColorEnum.Blue}
          title={
            <div className="flex gap-2">
              {currentDeviceIcon}
              <span className="text-textLink">{currentTitle}</span>
            </div>
          }
          isDisabled={false}
          classNameDropdown="p-0 pointer-events-auto"
        >
          <h5
            onClick={() => setCurrentDeviceMode(deviceModes.Desktop)}
            className="flex cursor-pointer items-center justify-between p-3 hover:bg-bgSecondary"
          >
            <div className="inline-flex items-center gap-1.5">
              <Monitor01Icon className="h-5 w-5 fill-iconSecondary" />
              {t('settings_options.device_appearance.desktop')}
            </div>
            {isDesktop && <CheckmarkIcon />}
          </h5>
          <h5
            onClick={() => setCurrentDeviceMode(deviceModes.Mobile)}
            className="flex cursor-pointer items-center justify-between p-3 hover:bg-bgSecondary"
          >
            <div className="inline-flex items-center gap-1.5">
              <Phone01Icon className="h-5 w-5 stroke-iconSecondary" />
              {t('settings_options.device_appearance.mobile')}
            </div>
            {isMobile && <CheckmarkIcon />}
          </h5>
        </BadgeDropdown>
        {isMobile && (
          <PhoneRotate01Icon
            onClick={toggleScreenOrientation}
            className={twMerge(
              'pointer-events-auto h-5 w-5 cursor-pointer fill-iconTertiary hover:fill-iconPrimary',
              isLandscape && 'fill-iconPrimary',
            )}
          />
        )}
      </div>
      <div className="inline-flex items-start justify-end">
        <GhostIconButton
          className="pointer-events-auto [&>svg]:h-10 [&>svg]:w-10"
          onClick={handleClosePreview}
          icon={<XCircleFill02Icon />}
          size={ButtonSizeEnum.Small}
        />
      </div>
    </nav>
  )
}
